import { DOMPurify as DOMPurifyWithTargetBlankHook } from "component-library/src/components/Molecules/RichTextArea/utils";

/** Same pattern as FurtherInformation for auto-linking bare URLs in text nodes. */
const URLS_PATTERN =
  /(https?:\/\/)?(www\.)[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)|(https?:\/\/)?(www\.)?(?!ww)[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_+.~#?&/=]*)/gi;

export const HTML_TO_TEXT_REPLACEMENTS: Array<Record<string, string>> = [
  { "<br>": "\n" },
  { "&amp;": "&" },
  { "&lt;": "<" },
  { "&gt;": ">" },
];

const EXPANDABLE_TEXT_AREA_ALLOWED_TAGS = [
  "a",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "ul",
  "ol",
  "li",
  "br",
  "p",
];

const EXPANDABLE_TEXT_AREA_PURIFY_CONFIG = {
  ALLOWED_TAGS: EXPANDABLE_TEXT_AREA_ALLOWED_TAGS,
  ALLOWED_ATTR: ["href"],
  ALLOWED_URI_REGEXP: /^(?:(?:https?:|mailto:))/i,
};

function purifyExpandableTextAreaHtml(html: string): string {
  return DOMPurifyWithTargetBlankHook.sanitize(
    html,
    EXPANDABLE_TEXT_AREA_PURIFY_CONFIG
  );
}

function newlinesToBr(value: string): string {
  return value.replaceAll("\r\n", "\n").replaceAll("\n", "<br />");
}

/**
 * Sanitize expandableTestArea for API storage and for re-display after load.
 * Converts newlines to `<br />`, then applies DOMPurify with a strict allow-list.
 */
export function sanitizeExpandableTextAreaForStorage(value: string): string {
  return purifyExpandableTextAreaHtml(newlinesToBr(value));
}

/**
 * Stored expandableTextArea use `<br />` for line breaks (see `sanitizeExpandableTextAreaForStorage`).
 * For a plain textarea editor, convert those back to `\n` so breaks match read-only HTML rendering.
 * Other allowed tags are left unchanged.
 */
export function expandableTextAreaStorageHtmlToDraftString(
  html: string
): string {
  return html.replaceAll("\r\n", "\n").replaceAll(/<br\s*\/?>/gi, "\n");
}

/**
 * Applies ordered string replacements.
 * Example: sanitizeTextForRequestPayload(value, [{ "<br>": "\n" }, { "&amp;": "&" }])
 */
export function sanitizeTextForRequestPayload(
  value: string,
  replacements: ReadonlyArray<Record<string, string>>
): string {
  return replacements
    .flatMap((replacementMap) =>
      Object.entries(replacementMap).filter(([find]) => find.length > 0)
    )
    .reduce(
      (result, [find, replaceWith]) => result.replaceAll(find, replaceWith),
      value
    );
}

function linkifyTextNodesInElement(root: HTMLElement): void {
  const textNodes: Text[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    const t = node as Text;

    if (t.parentElement?.closest("a")) {
      continue;
    }

    textNodes.push(t);
  }

  for (const textNode of textNodes) {
    const text = textNode.textContent ?? "";

    if (!text) {
      continue;
    }

    const re = new RegExp(URLS_PATTERN.source, URLS_PATTERN.flags);
    const matches = [...text.matchAll(re)];

    if (matches.length === 0) {
      continue;
    }

    if (!textNode.parentNode) {
      continue;
    }

    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    for (const m of matches) {
      const idx = m.index ?? 0;
      const raw = m[0];

      if (idx > lastIndex) {
        fragment.appendChild(
          document.createTextNode(text.slice(lastIndex, idx))
        );
      }

      const a = document.createElement("a");
      a.appendChild(document.createTextNode(raw));
      a.href = /^https?:\/\//i.test(raw) ? raw : `http://${raw}`;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      fragment.appendChild(a);
      lastIndex = idx + raw.length;
    }

    if (lastIndex < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
    }

    textNode.replaceWith(fragment);
  }
}

/**
 * Sanitize, linkify bare URLs in text nodes only, then sanitize again.
 * For read-only UI; uses `document` (browser / jsdom).
 */
export function sanitizeTextForDisplay(value: string): string {
  const sanitizedHtmlBeforeLinkification =
    sanitizeExpandableTextAreaForStorage(value);
  const container = document.createElement("div");
  container.innerHTML = sanitizedHtmlBeforeLinkification;
  linkifyTextNodesInElement(container);

  return purifyExpandableTextAreaHtml(container.innerHTML);
}

/** Visible character count for expand/collapse thresholds (ignores HTML tags). */
export function expandableTextAreaPlainTextLength(html: string): number {
  const div = document.createElement("div");
  div.innerHTML = html;

  return div.textContent?.length ?? 0;
}

/**
 * Collapsed + "more" when over char budget or structural hints suggest more than
 * `structuralLineHint` visual rows (see `<br>` / blocks). Defaults match a 3-line clamp.
 */
export function expandableTextAreaExceedsShowMoreThreshold(
  html: string,
  charLimit: number,
  structuralLineHint: number = 3
): boolean {
  if (expandableTextAreaPlainTextLength(html) > charLimit) {
    return true;
  }

  if (typeof document === "undefined") {
    return false;
  }

  const div = document.createElement("div");
  div.innerHTML = html;

  if (div.querySelectorAll("br").length >= structuralLineHint) {
    return true;
  }

  if (div.querySelectorAll("p").length > structuralLineHint) {
    return true;
  }

  if (div.querySelectorAll("li").length > structuralLineHint) {
    return true;
  }

  return false;
}
